/**
 * Add to My Subscription (PDP) — standalone, no bundle dependency.
 *
 * On a product page, for a logged-in customer:
 *  - the "Add to My Subscription" button shows from a cached flag (localStorage), so a plain
 *    product view costs no request; the flag is resolved once by GET {endpoint}
 *  - the full list of upcoming Recharge orders (charges) is fetched lazily, on the first open
 *  - the popup shows the selected variant, a quantity control, a subscription/one-time toggle,
 *    and the list of upcoming orders to choose from
 *  - confirming POSTs the product to the chosen order.
 *
 * Texts and the endpoint come from data-* attributes (Theme settings -> Subscriptions).
 */
(function () {
  if (window.customElements && customElements.get('subscription-pdp')) return;

  // Poll the orders endpoint every POLL_MS to detect when the product actually lands
  // in the order. PENDING_MAX_MS is a HARD ceiling: once reached we stop polling and
  // resolve the pending state so the spinner can never run forever (even across reloads).
  const POLL_MS = 5000;
  const PENDING_MAX_MS = 60000;
  const POLL_BACKGROUND_MS = 20000;
  const FLAG_TTL_MS = 43200000;
  const FLAG_TTL_NEGATIVE_MS = 3600000;
  const CHARGES_TTL_MS = 120000;
  const OPTIMISTIC_MAX_MS = 600000;

  class SubscriptionPdp extends HTMLElement {
    constructor() {
      super();
      this.endpoint = this.dataset.endpoint;
      this.customerId = this.dataset.customerId;
      this.productId = this.dataset.productId;
      this.section = this.dataset.section;
      this.loggedIn = this.dataset.loggedIn === 'true';
      this.enablePdp = this.dataset.enablePdp === 'true';
      this.enableQuickshop = this.dataset.enableQuickshop === 'true';
      this.pollEnable = this.dataset.pollEnable !== 'false';
      this.autoclose = this.dataset.autoclose === 'true';
      this.instantSuccess = this.dataset.instantSuccess !== 'false';
      this.ordersCount = this.dataset.ordersCount || '';
      this.toggleEnable = this.dataset.toggleEnable !== 'false';
      this.subscriptionOnly = this.dataset.subscriptionOnly === 'true';
      this.groupItems = this.dataset.groupItems !== 'false';
      this.qtyDiscount = this.dataset.qtyDiscount !== 'false';
      // Shop-wide default % off for one-time adds (shop.onetime_discount shop metafield).
      this.onetimeDiscount = parseFloat(this.dataset.onetimeDiscount) || 0;
      this.productHasSub = this.dataset.productHasSub === 'true';

      this.text = {
        subscriptionLabel: this.dataset.textSubscriptionLabel || 'Add to every order',
        onetimeLabel: this.dataset.textOnetimeLabel || 'Add to next order only',
        success: this.dataset.textSuccess || 'Product added to your subscription',
        processing: this.dataset.textProcessing || 'It can take about 5–20 seconds to appear in your order.',
        error: this.dataset.textError || 'Something went wrong. Please try again.',
        empty: this.dataset.textEmpty || 'You have no upcoming subscription orders.',
        oos: this.dataset.textOos || 'Out of Stock',
      };

      this.charges = [];
      this.groups = [];
      this.selectedGroupIndex = null;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      // Context: the same markup is reused inside the Quick Shop popup, so decide
      // which setting governs this instance based on where it lives in the DOM.
      this.quickShopHost = this.closest('#quick-buy-modal');
      this.inQuickShop = !!this.quickShopHost;
      const enabled = this.inQuickShop ? this.enableQuickshop : this.enablePdp;
      if (!enabled) {
        this.style.display = 'none';
        return;
      }

      this.cacheDom();
      this.parseVariants();

      // Guest: wire the login link to return here after sign-in. Mirror the Recharge
      // widget so the prompt only shows when the current variant offers a subscription.
      if (!this.loggedIn) {
        this.setupLoginRedirect();
        this.initWidgetMirror();
        return;
      }

      if (!this.endpoint || !this.trigger || !this.popup) return;

      // Move the popup out of the product <form> so its inputs never leak into the
      // add-to-cart FormData. Inside Quick Shop the dialog is opened with showModal()
      // (top layer), so the popup must live inside the dialog to render above it.
      this.popupHost = this.inQuickShop ? this.quickShopHost : document.body;
      if (this.popup.parentElement !== this.popupHost) {
        this.popupHost.appendChild(this.popup);
      }

      this.bindEvents();
      this.initWidgetMirror();
      this.initChargesState();
    }

    disconnectedCallback() {
      // When the Quick Shop popup content is cleared the element is removed — tidy up
      // the relocated popup and the document/window listeners so nothing leaks.
      clearTimeout(this._closeTimer);
      clearTimeout(this._syncTimer);
      clearTimeout(this._onceTimer);
      clearTimeout(this._statusHideTimer);
      clearInterval(this._pollTimer);
      if (this._widgetRaf) cancelAnimationFrame(this._widgetRaf);
      if (this._widgetTimers) this._widgetTimers.forEach((t) => clearTimeout(t));
      if (this._widgetObserver) this._widgetObserver.disconnect();
      if (this.popup && this.popup.parentElement && this.popup.parentElement !== this) {
        this.popup.remove();
      }
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      if (this._onVariantChange) window.removeEventListener('custom:variant-changed', this._onVariantChange);
      if (this._onWidgetVariant) window.removeEventListener('custom:variant-changed', this._onWidgetVariant);
    }

    // Make the logged-out prompt's login link return to the current page after sign-in.
    setupLoginRedirect() {
      const links = this.querySelectorAll('.subscription-pdp__login-prompt a');
      if (!links.length) return;
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      links.forEach((a) => {
        const href = a.getAttribute('href') || '';
        const isLogin = /login|customer_authentication|\/account(\/|$)/i.test(href);
        if (!isLogin || /return_url=/i.test(href)) return;
        a.setAttribute('href', href + (href.indexOf('?') === -1 ? '?' : '&') + 'return_url=' + returnUrl);
      });
    }

    cacheDom() {
      this.trigger = this.querySelector('.subscription-pdp__trigger');
      this.popup = this.querySelector('.subscription-pdp__popup');
      this.variantBox = this.querySelector('.subscription-pdp__variant');
      this.qtyInput = this.querySelector('.subscription-pdp__qty-input');
      this.toggleInput = this.querySelector('.subscription-pdp__toggle-input');
      this.toggleText = this.querySelector('.subscription-pdp__toggle-text');
      this.chargesBox = this.querySelector('.subscription-pdp__charges');
      this.message = this.querySelector('.subscription-pdp__message');
      this.confirmBtn = this.querySelector('.subscription-pdp__confirm');
      this.statusBox = this.querySelector('.subscription-pdp__status');
      this.promptBox = this.querySelector('.subscription-pdp__login-prompt');

      // PDP product form (rendered by the theme) — source of the selected variant + qty
      this.form = document.getElementById('product-form-' + this.section);
    }

    parseVariants() {
      this.variants = [];
      const dataEl = this.querySelector('.subscription-pdp__variants-data');
      if (!dataEl) return;
      try {
        const parsed = JSON.parse(dataEl.textContent);
        this.variants = parsed.variants || [];
      } catch (e) {
        console.error('subscription-pdp: invalid variants JSON', e);
      }
    }

    bindEvents() {
      this.trigger.addEventListener('click', () => this.open());

      this.popup.querySelectorAll('[data-spdp-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );

      this._onKeydown = (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      };
      document.addEventListener('keydown', this._onKeydown);

      this.toggleInput.addEventListener('change', () => {
        this.updateToggleText();
        this.renderVariant();
      });

      this.popup.querySelectorAll('[data-spdp-qty]').forEach((btn) =>
        btn.addEventListener('click', () => this.stepQty(btn.dataset.spdpQty))
      );

      // Re-render the price when the quantity is typed (volume discount may change).
      this.qtyInput.addEventListener('input', () => this.renderVariant());

      this.confirmBtn.addEventListener('click', () => this.submit());

      // Refresh the popup's variant info / price / type mode on change. Show/hide is driven
      // entirely by the observer mirror (initWidgetMirror), not by tracking variant events.
      this._onVariantChange = () => this.refreshOpenPopup();
      window.addEventListener('custom:variant-changed', this._onVariantChange);
    }

    /* ---------- has-subscriptions flag (cached) ---------- */

    flagKey() {
      return 'spdp_has_subs_' + (this.customerId || 'anon');
    }

    // Null when the flag must be re-resolved: missing, expired, or written before the
    // customer's latest order (a subscription bought since then wouldn't be reflected).
    loadFlag() {
      let data = null;
      try {
        const raw = window.localStorage.getItem(this.flagKey());
        data = raw ? JSON.parse(raw) : null;
      } catch (e) {
        data = null;
      }
      if (!data || typeof data.has !== 'boolean') return null;
      if (String(data.orders) !== String(this.ordersCount)) return null;
      const ttl = data.has ? FLAG_TTL_MS : FLAG_TTL_NEGATIVE_MS;
      if (Date.now() - (data.ts || 0) > ttl) return null;
      return data;
    }

    saveFlag(has) {
      try {
        window.localStorage.setItem(
          this.flagKey(),
          JSON.stringify({ has: !!has, orders: String(this.ordersCount), ts: Date.now() })
        );
      } catch (e) {
        /* storage unavailable — the flag is simply re-resolved on the next page view */
      }
    }

    /* ---------- data ---------- */

    // Trigger visibility comes from the cached flag, so viewing a product costs no request.
    initChargesState() {
      const cached = this.loadFlag();
      if (cached) {
        this.hasCharges = cached.has;
        this.updateTriggerVisibility();
        // An add still awaiting confirmation needs real data, or it would look unsynced.
        if (this.loadPending().some((e) => String(e.productId) === String(this.productId))) {
          this.fetchCharges().then(() => this.resumePending());
        } else {
          this.resumePending();
        }
        return;
      }
      this.fetchCharges().then(() => this.resumePending());
    }

    // With chargeId only that one order is re-read (`charge_id` param) and merged into the
    // list we hold, instead of pulling every subscription.
    fetchCharges(chargeId) {
      let url = this.endpoint;
      if (chargeId) {
        url += (url.indexOf('?') === -1 ? '?' : '&') + 'charge_id=' + encodeURIComponent(chargeId);
      }
      return fetch(url, {
        method: 'GET',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        credentials: 'same-origin',
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => {
          const list = Array.isArray(data) ? data : data && typeof data === 'object' ? [data] : [];
          this._fetchFailed = false;
          if (chargeId) {
            this.mergeCharge(list, chargeId);
          } else {
            this.charges = list;
            this.chargesLoaded = true;
            this._chargesTs = Date.now();
          }
          // Only orders that contain a subscription are valid targets — a customer whose
          // upcoming orders are all one-time has nothing to add to, so don't show the trigger.
          // Computed from server data only, before optimistic items go back in.
          const found = this.buildGroups().length > 0;
          if (chargeId && !this.chargesLoaded) {
            // One order proves orders exist, never that they don't — must not hide the button.
            if (found) {
              this.hasCharges = true;
              this.saveFlag(true);
            }
          } else {
            this.hasCharges = found;
            this.saveFlag(found);
          }
          this.applyOptimistic();
          this.updateTriggerVisibility();
          return this.charges;
        })
        .catch((e) => {
          console.error('subscription-pdp: failed to load charges', e);
          this._fetchFailed = true;
          return this.charges || [];
        });
    }

    // Splice a re-read order back into its slot; an empty response means it's gone.
    mergeCharge(list, chargeId) {
      // Several orders back means charge_id was ignored — take it as a full read rather than
      // splicing a whole list into one slot, which would duplicate the other orders.
      if (list.length > 1) {
        this.charges = list;
        this.chargesLoaded = true;
        this._chargesTs = Date.now();
        return;
      }
      // A single order that isn't the one asked for tells us nothing — leave the list alone.
      if (list.length === 1 && String(this.chargeIdOf(list[0])) !== String(chargeId)) return;
      const current = this.charges || [];
      const index = current.findIndex((c) => String(this.chargeIdOf(c)) === String(chargeId));
      if (index === -1) {
        this.charges = current.concat(list);
        return;
      }
      this.charges = current.slice(0, index).concat(list, current.slice(index + 1));
    }

    // Only the field the backend will add — never guessed from another key, or we could send
    // charge_id to an endpoint that ignores it. Null until then, so reads stay full.
    chargeIdOf(charge) {
      const id = charge && charge.charge_id;
      return id == null || id === '' ? null : id;
    }

    // Fetch the list for an open popup: on the first open, and again once ours went stale.
    loadChargesForOpen() {
      const fresh = this.chargesLoaded && Date.now() - (this._chargesTs || 0) < CHARGES_TTL_MS;
      if (fresh || this._loadingCharges) return;
      this._loadingCharges = true;
      this.showChargesLoading();
      this.fetchCharges().then(() => {
        this._loadingCharges = false;
        if (!this.isOpen() || this.added) return;
        if (this._fetchFailed && !this.chargesLoaded) {
          this.showChargesError();
          return;
        }
        this.renderCharges();
      });
    }

    showChargesLoading() {
      if (!this.chargesBox) return;
      this.confirmBtn.disabled = true;
      this.chargesBox.innerHTML =
        '<p class="subscription-pdp__loading">' +
        '<span class="subscription-pdp__spinner" aria-hidden="true"></span>' +
        '<span>Loading your orders…</span></p>';
    }

    showChargesError() {
      if (!this.chargesBox) return;
      this.confirmBtn.disabled = true;
      this.chargesBox.innerHTML =
        '<p class="subscription-pdp__empty">' + this.escape(this.text.error) + '</p>';
    }

    getSelectedVariantId() {
      // Prefer the variant selector's resolved variant: it's set synchronously on every change
      // and stays correct through the theme's many variant re-renders/fetches, whereas the
      // form's [name="id"] value is updated a tick later on some paths.
      const vr = this.scope().querySelector('mtn-variant-radios');
      if (vr && vr.currentVariant && vr.currentVariant.id != null) return String(vr.currentVariant.id);
      const input = this.form && this.form.querySelector('[name="id"]');
      if (input && input.value) return String(input.value);
      return this.variants.length ? String(this.variants[0].id) : null;
    }

    getSelectedVariant() {
      const id = this.getSelectedVariantId();
      return this.variants.find((v) => String(v.id) === id) || this.variants[0] || null;
    }

    // Whether our UI should be available for the currently selected variant. The button is
    // NOT gated on the variant having a selling plan: a variant without one can still be
    // added to an upcoming order — the popup then locks the type to one-time. Only
    // out-of-stock hides the button. This store's variant switching fires a storm of
    // re-renders/fetches and toggles the Recharge widget DOM inconsistently, so we do NOT
    // read that DOM.
    isVariantOrderable() {
      // Out of stock: the theme marks the add-to-cart button "is-invisible" (shows "Notify
      // me"). Use that precise signal as the OOS check (scoped to this product's form).
      const scope = this.scope();
      const addBtn = (this.form && this.form.querySelector('[name="add"]')) || scope.querySelector('[name="add"]');
      if (addBtn && addBtn.classList.contains('is-invisible')) return false;
      return !!this.getSelectedVariant();
    }

    resolveTypeMode() {
      const variant = this.getSelectedVariant();
      if (variant && !variant.has_sub) return 'onetime';
      if (!this.toggleEnable || this.subscriptionOnly) return 'subscription';
      return 'toggle';
    }

    effectiveIsSubscription() {
      const mode = this.resolveTypeMode();
      if (mode === 'onetime') return false;
      if (mode === 'subscription') return true;
      return this.toggleInput.checked;
    }

    // Keep the trigger / login prompt in sync with the selected variant. Because the theme
    // re-renders and re-fetches heavily on every switch, a single event can fire before the
    // selection settles — so we re-evaluate on THREE signals and always read the reliable,
    // always-current selection (mtn-variant-radios.currentVariant / [name="id"]):
    //   - the theme's `custom:variant-changed` event;
    //   - a MutationObserver on the product area (the re-render churn IS our change signal);
    //   - a few delayed checks that catch the async first render after load.
    // Everything funnels through one rAF, so repeated churn coalesces into a single update.
    initWidgetMirror() {
      // Scope to THIS product: the Quick Shop dialog, or the product's section on the PDP
      // (never the whole document, so a cart-drawer widget can't be mistaken for this product).
      this.widgetScope =
        this.inQuickShop && this.quickShopHost
          ? this.quickShopHost
          : (this.form && this.form.closest('.shopify-section')) || this;

      this._onWidgetVariant = () => this.scheduleWidgetUpdate();
      window.addEventListener('custom:variant-changed', this._onWidgetVariant);

      this.scheduleWidgetUpdate();
      this._widgetTimers = [300, 800, 1500, 3000].map((ms) =>
        setTimeout(() => this.scheduleWidgetUpdate(), ms)
      );
    }

    runWidgetUpdate() {
      if (this.loggedIn) {
        this.updateTriggerVisibility();
        if (this.isOpen()) {
          if (!this.isVariantOrderable()) {
            if (this.popup.classList.contains('is-open')) this.close();
          } else if (
            this.getSelectedVariantId() !== this._renderedVariantId ||
            this.resolveTypeMode() !== this._typeMode
          ) {
            this.refreshOpenPopup();
          }
        }
      } else {
        this.updatePromptVisibility();
      }
    }

    scheduleWidgetUpdate() {
      if (this._widgetRaf) return;
      this._widgetRaf = requestAnimationFrame(() => {
        this._widgetRaf = null;
        this.attachWidgetObserver();
        this.runWidgetUpdate();
      });
    }

    // Observe the product area once: any variant switch re-renders the price / buttons / qty
    // in here, and each such mutation is our signal to re-check the selection (debounced by the
    // rAF in scheduleWidgetUpdate). We watch this subtree only, not the whole page. Our own
    // updates change only 'hidden'/'style'/innerHTML of our nodes, which can't loop this
    // observer (it filters to childList + class attributes).
    attachWidgetObserver() {
      if (this._widgetObserver) return;
      const target =
        this.widgetScope || (this.form && this.form.closest('.shopify-section')) || document;
      if (!target || !window.MutationObserver) return;
      this._widgetObserver = new MutationObserver(() => this.scheduleWidgetUpdate());
      this._widgetObserver.observe(target, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // Show the trigger whenever the customer has upcoming orders and the variant is in stock.
    // Variants without a subscription plan still get the button — they're added as one-time.
    updateTriggerVisibility() {
      if (!this.trigger) return;
      const show = this.hasCharges && this.isVariantOrderable();
      this.trigger.hidden = !show;
      // Drive visibility from our own reliable decision (charges + stock) rather than the
      // CSS that keys off the flaky Recharge widget DOM (":has(.rc-radio__input)"). An
      // inline style wins over those rules in both directions, so it can't get stuck.
      this.trigger.style.display = show ? 'inline-flex' : 'none';
    }

    // Guest: hide the login prompt for out-of-stock variants.
    updatePromptVisibility() {
      if (!this.promptBox) return;
      this.promptBox.style.display = this.isVariantOrderable() ? '' : 'none';
    }

    /* ---------- rendering ---------- */

    renderVariant() {
      const variant = this.getSelectedVariant();
      if (!variant) {
        this.variantBox.innerHTML = '';
        return;
      }
      const productTitle = this.dataset.productTitle || '';
      const showVariantTitle = variant.title && variant.title.toLowerCase() !== 'default title';
      this.variantBox.innerHTML = `
        ${this.imgTag(variant.image, 'subscription-pdp__variant-img', 60)}
        <div class="subscription-pdp__variant-info">
          <span class="subscription-pdp__variant-title">${this.escape(productTitle)}</span>
          ${showVariantTitle ? `<span class="subscription-pdp__variant-option">${this.escape(variant.title)}</span>` : ''}
          ${this.priceMarkup(variant)}
        </div>`;
    }

    // Price shown for the selected variant:
    //  - subscription mode  -> the subscription (selling-plan) price, struck retail + % off;
    //  - one-time mode       -> the volume/quantity-break price for the current QTY (mirrors
    //                           the product page, e.g. "3+ for $21.95"), if enabled.
    priceMarkup(variant) {
      const isSubscription = this.toggleInput.checked;
      const base = variant.price_amount != null ? variant.price_amount / 100 : null;

      if (isSubscription) {
        const hasDiscount =
          variant.sub_price && variant.sub_amount != null && variant.price_amount != null && variant.sub_amount < variant.price_amount;
        if (hasDiscount) {
          const percent = Math.round((1 - variant.sub_amount / variant.price_amount) * 100);
          return this.priceBlock(variant.sub_price, variant.price, percent, '');
        }
        return variant.price ? `<span class="subscription-pdp__variant-price">${this.escape(variant.price)}</span>` : '';
      }

      // One-time: the shop.onetime_discount % and the product's volume pricing compete — the
      // bigger discount wins. The qty-discount setting gates only 2+ tiers; a 1-unit tier
      // always applies.
      const qty = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
      const tiers = this.onetimeTiers(variant);
      let upcoming = null;
      for (let i = 0; i < tiers.length; i++) {
        if (tiers[i].from > qty) { upcoming = tiers[i]; break; } // next threshold not yet reached
      }
      const salePrice = this.onetimeUnitPrice(variant, qty);

      if (salePrice != null) {
        const percent = Math.round((1 - salePrice / base) * 100);
        // Keep the "(N+ for ...)" hint only while the upcoming tier beats the current price.
        const note =
          upcoming && upcoming.price < salePrice
            ? '(' + upcoming.from + '+ for ' + this.formatMoney(upcoming.price) + ')'
            : '';
        return this.priceBlock(this.formatMoney(salePrice), variant.price, percent, note);
      }
      // No discount at this qty — show base "EACH" + the upcoming-tier hint.
      if (upcoming && variant.price) {
        const note = '(' + upcoming.from + '+ for ' + this.formatMoney(upcoming.price) + ')';
        return `
          <span class="subscription-pdp__variant-price">
            <span class="subscription-pdp__price-each">${this.escape(variant.price)} EACH</span>
            <span class="subscription-pdp__price-tier">${this.escape(note)}</span>
          </span>`;
      }
      return variant.price ? `<span class="subscription-pdp__variant-price">${this.escape(variant.price)}</span>` : '';
    }

    // Volume tiers that may apply: the qty-discount setting gates only 2+ ones.
    onetimeTiers(variant) {
      const base = variant.price_amount != null ? variant.price_amount / 100 : null;
      if (base == null) return [];
      const tiers = this.getQtyTiers(variant).filter((t) => t.price < base);
      return this.qtyDiscount ? tiers : tiers.filter((t) => t.from <= 1);
    }

    // Best one-time unit price at this qty — volume tier vs shop.onetime_discount, bigger
    // discount wins. Null when neither applies.
    onetimeUnitPrice(variant, qty) {
      const base = variant.price_amount != null ? variant.price_amount / 100 : null;
      if (base == null) return null;
      let price = null;
      this.onetimeTiers(variant).forEach((t) => {
        if (qty >= t.from) price = t.price;
      });
      if (this.onetimeDiscount > 0 && this.onetimeDiscount < 100) {
        const meta = base * (1 - this.onetimeDiscount / 100);
        if (price == null || meta < price) price = meta;
      }
      return price;
    }

    // The unit price the popup just quoted, so a faked row can't contradict it.
    quotedUnitPrice(variant, qty, isSubscription) {
      const base = variant.price_amount != null ? variant.price_amount / 100 : null;
      if (isSubscription) {
        const discounted =
          variant.sub_amount != null && variant.price_amount != null && variant.sub_amount < variant.price_amount;
        return discounted ? variant.sub_amount / 100 : base;
      }
      const sale = this.onetimeUnitPrice(variant, qty);
      return sale != null ? sale : base;
    }

    priceBlock(salePrice, wasPrice, percent, note) {
      return `
        <span class="subscription-pdp__variant-price">
          <span class="subscription-pdp__price-sub">${this.escape(salePrice)}</span>
          ${wasPrice ? `<span class="subscription-pdp__price-was">${this.escape(wasPrice)}</span>` : ''}
          ${percent > 0 ? `<span class="subscription-pdp__price-badge">${percent}% OFF</span>` : ''}
          ${note ? `<span class="subscription-pdp__price-tier">${this.escape(note)}</span>` : ''}
        </span>`;
    }

    // Reads the product page's volume-pricing data ("data-discount" on .c-price) and returns
    // the configured tiers (sorted ascending), or []. Format:
    //   discount:<group>:<variantId|product>:<from>-<price>:<from>-<price>...
    getQtyTiers(variant) {
      const priceEl = this.scope().querySelector('.c-price[data-discount]');
      if (!priceEl) return [];
      const parts = (priceEl.getAttribute('data-discount') || '').split(':');
      const group = (priceEl.getAttribute('data-customer-group') || '').toLowerCase();
      if (parts.length <= 3 || parts[0] !== 'discount') return [];
      if (parts[1].toLowerCase() !== group) return [];
      const target = parts[2];
      if (target !== 'product' && String(parseInt(target, 10)) !== String(variant && variant.id)) return [];
      return parts
        .slice(3)
        .map((r) => {
          const seg = String(r).split('-');
          return { from: parseInt(seg[0], 10), price: parseFloat(seg[1]) };
        })
        .filter((t) => !isNaN(t.from) && !isNaN(t.price))
        .sort((a, b) => a.from - b.from);
    }

    // Split every charge into one selectable group per distinct subscription interval. A
    // charge whose subscription items have different intervals becomes several cards — each a
    // separate radio with its own interval badge — and the order's common info (date /
    // address / payment) is intentionally repeated on each. One-time items do NOT get their
    // own radio: they're folded into the first subscription group (and badged per item). A
    // charge that has only one-time items (no subscription) is not a valid target and is
    // omitted entirely.
    buildGroups() {
      const groups = [];
      (this.charges || []).forEach((charge) => {
        this.splitCharge(charge).forEach((g) => groups.push(g));
      });
      return groups;
    }

    splitCharge(charge) {
      const lineItems = charge.line_items || [];
      const byKey = {};
      const groups = [];
      const onetimes = [];
      lineItems.forEach((li) => {
        const freq = li.order_interval_frequency;
        const isSub = li.type !== 'onetime' && !!freq;
        if (!isSub) {
          onetimes.push(li);
          return;
        }
        const key = 's:' + freq + '|' + (li.order_interval_unit || '');
        if (!byKey[key]) {
          byKey[key] = {
            charge: charge,
            interval: { frequency: freq, unit: li.order_interval_unit },
            lineItems: [],
          };
          groups.push(byKey[key]);
        }
        byKey[key].lineItems.push(li);
      });
      // Subscription groups: ascending frequency.
      groups.sort((a, b) => a.interval.frequency - b.interval.frequency);
      // Fold one-time items into the first subscription group. A charge with NO subscription
      // items isn't a valid "add to subscription" target, so it produces no group (skipped).
      if (onetimes.length && groups.length) {
        groups[0].lineItems = groups[0].lineItems.concat(onetimes);
      }
      return groups;
    }

    renderCharges() {
      // Never fall through to the "no upcoming orders" copy before the list has arrived.
      if (!this.chargesLoaded) {
        this.showChargesLoading();
        return;
      }
      this.groups = this.buildGroups();
      if (!this.groups.length) {
        this.chargesBox.innerHTML = `<p class="subscription-pdp__empty">${this.escape(this.text.empty)}</p>`;
        return;
      }

      this.chargesBox.innerHTML = this.groups
        .map((group, index) => {
          const charge = group.charge;
          const addr = charge.shipping_address || charge.billing_address || {};
          const name = [addr.first_name, addr.last_name].filter(Boolean).join(' ');
          const addressLine = [addr.address1, addr.city, addr.province, addr.zip].filter(Boolean).join(', ');
          const lineItems = group.lineItems || [];
          // Every item in a group shares the same interval (or all one-time), so the badge
          // is shown once at the group level instead of per item.
          const badge = group.interval
            ? this.intervalBadge(group.interval.frequency, group.interval.unit)
            : '<span class="subscription-pdp__charge-onetime">One-time</span>';
          const items = this.groupLineItems(lineItems)
            .map((li) => this.chargeItemRow(li, charge.currency))
            .join('');

          const itemCount = lineItems.reduce((sum, li) => sum + (li.quantity || 1), 0);

          return `
            <label class="subscription-pdp__charge">
              <input type="radio" name="spdp-charge" class="subscription-pdp__charge-radio" value="${index}" data-index="${index}">
              <span class="subscription-pdp__charge-body">
                <span class="subscription-pdp__charge-head">
                  <span class="subscription-pdp__charge-meta">
                    <span class="subscription-pdp__charge-key">Next order:</span>
                    <span class="subscription-pdp__charge-val subscription-pdp__charge-date">${this.formatDate(charge.scheduled_at)}</span>
                  </span>
                  ${badge}
                </span>
                ${
                  name || addressLine
                    ? `<span class="subscription-pdp__charge-meta">
                         <span class="subscription-pdp__charge-key">Ship to:</span>
                         <span class="subscription-pdp__charge-val subscription-pdp__charge-val--address">${this.escape([name, addressLine].filter(Boolean).join(', '))}</span>
                       </span>`
                    : ''
                }
                ${this.paymentMarkup(charge.payment_method)}
                <span class="subscription-pdp__charge-items">
                  <span class="subscription-pdp__items">
                    <span class="subscription-pdp__items-head">
                      <span class="subscription-pdp__items-title">In this order<span class="subscription-pdp__items-count">${itemCount} ${itemCount === 1 ? 'item' : 'items'}</span></span>
                      <span class="subscription-pdp__items-col">Qty</span>
                      <span class="subscription-pdp__items-col">Subtotal</span>
                    </span>
                    ${items}
                  </span>
                </span>
              </span>
            </label>`;
        })
        .join('');

      this.chargesBox.querySelectorAll('.subscription-pdp__charge-radio').forEach((radio) =>
        radio.addEventListener('change', () => {
          if (this.added) return;
          this.selectedGroupIndex = parseInt(radio.value, 10);
          this.confirmBtn.disabled = false;
        })
      );

      // Pre-select the first option so the customer can confirm in one click.
      // (After a successful add, onPendingDone() re-renders and then clears this on purpose.)
      const firstRadio = this.chargesBox.querySelector('.subscription-pdp__charge-radio');
      if (firstRadio && !this.added) {
        firstRadio.checked = true;
        this.selectedGroupIndex = parseInt(firstRadio.value, 10);
        this.confirmBtn.disabled = false;
      }
    }

    // One row of the "In this order" table: product (image + title + variant), quantity, and
    // the line subtotal (unit price × quantity, from the charge data). The subscription
    // interval lives at the group level, so only one-time items carry a per-item badge here.
    chargeItemRow(li, currency) {
      const price = this.lineMoney(li);
      const badge = li.type === 'onetime' ? '<span class="subscription-pdp__charge-onetime">One-time</span>' : '';
      const meta = badge ? `<span class="subscription-pdp__item-meta">${badge}</span>` : '';
      // Variant title (size/flavor). Not in the charge data yet — the backend will add it —
      // so render it only when present and not the placeholder "Default Title".
      const variant = li.variant_title && String(li.variant_title).toLowerCase() !== 'default title'
          ? `<span class="subscription-pdp__item-variant">${this.escape(li.variant_title)}</span>`
          : '';
      return `
        <span class="subscription-pdp__item">
          <span class="subscription-pdp__item-product">
            ${this.imgTag(li.image, 'subscription-pdp__item-img', 40)}
            <span class="subscription-pdp__item-text">
              <span class="subscription-pdp__item-title">${this.escape(li.title || '')}</span>
              ${variant}
              ${meta}
            </span>
          </span>
          <span class="subscription-pdp__item-qty">${li.quantity || 1}</span>
          <span class="subscription-pdp__item-price">${this.escape(this.formatMoney(price, currency))}</span>
        </span>`;
    }

    // Render an <img> only when there is a usable src; hide it if it fails to load
    // so a missing/broken image never shows a broken-image placeholder.
    imgTag(src, className, size) {
      if (!src || typeof src !== 'string') return '';
      return `<img class="${className}" src="${this.escape(src)}" alt="" width="${size}" height="${size}" loading="lazy" onerror="this.remove()">`;
    }

    intervalBadge(freq, unit) {
      const plural = freq > 1 ? 's' : '';
      return `<span class="subscription-pdp__charge-interval">Every ${this.escape(String(freq))} ${this.escape(unit || '')}${plural}</span>`;
    }

    // Merge identical products (same product + variant) into one row, summing quantity.
    // Items missing an id stay separate so unrelated products are never merged by mistake.
    groupLineItems(lineItems) {
      const list = lineItems || [];
      if (!this.groupItems) return list;
      const map = {};
      const order = [];
      list.forEach((li, i) => {
        const pid = li.shopify_product_id;
        const vid = li.shopify_variant_id;
        const canGroup = pid != null && pid !== '' && vid != null && vid !== '';
        // Include the type so a one-time line is never merged with a subscription line of
        // the same product/variant — they're shown (and badged) separately.
        const key = canGroup ? 'g:' + (li.type || '') + '|' + pid + '|' + vid : 'u:' + i;
        if (map[key]) {
          // Sum the subtotals before touching quantity — lineMoney may derive one from it.
          const sum = this.lineMoney(map[key]) + this.lineMoney(li);
          map[key].quantity = (map[key].quantity || 0) + (li.quantity || 0);
          if (isNaN(sum)) delete map[key].total_price;
          else map[key].total_price = sum;
        } else {
          map[key] = Object.assign({}, li);
          order.push(key);
        }
      });
      return order.map((k) => map[k]);
    }

    // A line's subtotal from whichever field the payload carries. NaN when neither does.
    lineMoney(li) {
      if (li.total_price != null) return Number(li.total_price);
      if (li.unit_price != null) return Number(li.unit_price) * (li.quantity || 1);
      return NaN;
    }

    // Payment method block: a "Payment:" label (matching the other rows) + card and expiry.
    paymentMarkup(pm) {
      if (!pm || !pm.details) return '';
      const d = pm.details;
      const brand = d.brand ? this.titleCase(d.brand) : this.titleCase(pm.type || 'Card');
      const card = brand + (d.last4 ? ' ••••' + this.escape(String(d.last4)) : '');
      const expires =
        d.exp_month && d.exp_year ? this.pad2(d.exp_month) + '/' + this.escape(String(d.exp_year)) : '';
      return (
        '<span class="subscription-pdp__charge-meta">' +
        '<span class="subscription-pdp__charge-key">Payment:</span>' +
        '<span class="subscription-pdp__charge-val subscription-pdp__charge-payment">' +
        '<span class="subscription-pdp__charge-payment-card">' + this.escape(card) + '</span>' +
        (expires ? '<span class="subscription-pdp__charge-payment-exp">Expires: ' + expires + '</span>' : '') +
        '</span>' +
        '</span>'
      );
    }

    formatMoney(amount, currency) {
      const n = Number(amount);
      if (isNaN(n)) return '';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
      } catch (e) {
        return '$' + n.toFixed(2);
      }
    }

    titleCase(str) {
      return String(str || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    pad2(value) {
      const n = parseInt(value, 10);
      return isNaN(n) ? this.escape(String(value)) : (n < 10 ? '0' + n : '' + n);
    }

    updateToggleText() {
      this.toggleText.textContent = this.toggleInput.checked
        ? this.text.subscriptionLabel
        : this.text.onetimeLabel;
    }

    /* ---------- validation (same as Add to Cart) ---------- */

    // DOM scope: the Quick Shop popup duplicates the product markup, so look up the
    // form / notification elements inside the dialog there, and in the document on the PDP.
    scope() {
      return this.inQuickShop && this.quickShopHost ? this.quickShopHost : document;
    }

    getById(id) {
      return this.scope().querySelector('[id="' + id + '"]');
    }

    validateSelection() {
      const notificationBlock = this.getById('FormNotification-' + this.section);
      const variantRadios = this.scope().querySelector('mtn-variant-radios');

      // 1) A variant option must be selected (multi-option products).
      if (variantRadios && typeof variantRadios.getOptions === 'function') {
        const options = variantRadios.getOptions();
        const notSelected = !options || options.some((o) => typeof o === 'undefined');
        if (notSelected) {
          const errorTpl = this.getById('FormNotification-error-' + this.section);
          if (notificationBlock && errorTpl) {
            notificationBlock.innerHTML = '';
            notificationBlock.append(errorTpl.cloneNode(true));
            notificationBlock.style.display = 'inline-block';
          }
          if (typeof variantRadios.toggleAddButton === 'function') {
            variantRadios.toggleAddButton(true, '', true);
          }
          return false;
        }
      }

      // 2) The selected variant must be in stock.
      const variant = this.getSelectedVariant();
      if (variant && variant.available === false) {
        this.showStockError(notificationBlock);
        return false;
      }

      // Clear any previous notification on success.
      if (notificationBlock) {
        notificationBlock.innerHTML = '';
        notificationBlock.style.display = 'none';
      }
      return true;
    }

    showStockError(notificationBlock) {
      if (!notificationBlock) return;
      notificationBlock.innerHTML =
        '<div class="product-form__notifications" role="alert" aria-live="assertive">' +
        '<span>' + this.escape(this.text.oos) + '</span></div>';
      notificationBlock.style.display = 'inline-block';
    }

    /* ---------- popup ---------- */

    isOpen() {
      return this.popup && !this.popup.hidden;
    }

    refreshOpenPopup() {
      if (!this.isOpen()) return;
      this.applyToggleMode();
      this.renderVariant();
      this._renderedVariantId = this.getSelectedVariantId();
    }

    applyToggleMode() {
      const prev = this._typeMode || null;
      const mode = this.resolveTypeMode();
      this._typeMode = mode;
      const wrap = this.toggleInput.closest('.subscription-pdp__toggle');
      if (mode === 'onetime') {
        this.toggleInput.checked = false;
        if (wrap) {
          wrap.style.display = '';
          wrap.classList.add('subscription-pdp__toggle_locked');
        }
      } else if (mode === 'subscription') {
        this.toggleInput.checked = true;
        if (wrap) wrap.style.display = 'none';
      } else {
        if (prev && prev !== 'toggle') this.toggleInput.checked = true;
        if (wrap) {
          wrap.style.display = '';
          wrap.classList.remove('subscription-pdp__toggle_locked');
        }
      }
      this.toggleInput.disabled = !!this._formLocked || mode !== 'toggle';
      this.updateToggleText();
    }

    open() {
      // Mirror the Add to Cart behaviour: block opening when a variant option is
      // not selected, or when the selected variant is out of stock.
      if (!this.validateSelection()) return;

      clearTimeout(this._syncTimer);
      this.added = false;
      this.setFormDisabled(false);
      this.resetMessage();
      this.confirmBtn.classList.remove('loading');
      this.confirmBtn.disabled = true;
      this.selectedGroupIndex = null;
      this._typeMode = null;
      this.toggleInput.checked = true;
      this.applyToggleMode();

      // Default quantity from the PDP quantity input (associated to the form via
      // a `form` attribute, so it lives outside the form element — read it by id).
      const pdpQty = this.scope().querySelector('[id="Quantity-' + this.section + '"]');
      this.qtyInput.value = pdpQty && Number(pdpQty.value) > 0 ? pdpQty.value : 1;

      this.renderVariant();
      this._renderedVariantId = this.getSelectedVariantId();
      this.renderCharges();

      clearTimeout(this._closeTimer);
      this.popup.hidden = false;
      this.popup.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('subscription-pdp-open');

      // Force a reflow so the entry transition runs from the initial state.
      void this.popup.offsetWidth;
      this.popup.classList.add('is-open');

      // The order list is only needed now that the popup is open.
      this.loadChargesForOpen();
    }

    close() {
      this.popup.classList.remove('is-open');
      this.popup.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('subscription-pdp-open');

      // Hide only after the exit animation finishes (fallback timeout if no transition).
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => {
        this.popup.hidden = true;
      }, 280);
    }

    stepQty(direction) {
      let value = parseInt(this.qtyInput.value, 10);
      if (isNaN(value)) value = 1;
      value = direction === 'plus' ? value + 1 : value - 1;
      if (value < 1) value = 1;
      this.qtyInput.value = value;
      this.renderVariant();
    }

    /* ---------- submit ---------- */

    submit() {
      const group = this.groups[this.selectedGroupIndex];
      if (!group) return;
      const charge = group.charge;
      const variant = this.getSelectedVariant();
      if (!charge || !variant) return;

      // Forced one-time (no selling plan) / forced subscription (subscription-only product) /
      // the customer's toggle — see effectiveIsSubscription.
      const isSubscription = this.effectiveIsSubscription();
      const quantity = Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
      this._submitted = { charge: charge, group: group, variant: variant, quantity: quantity, isSubscription: isSubscription };

      const payload = {
        type: isSubscription ? 'subscription' : 'onetime',
        address_id: charge.address_id,
        next_charge_scheduled_at: charge.scheduled_at,
        product_id: Number(this.productId),
        variant_id: Number(variant.id),
        quantity: quantity,
      };

      if (isSubscription) {
        // Use the interval of the chosen group; fall back to the charge's for a one-time group.
        const interval = group.interval || this.resolveInterval(charge);
        if (interval) {
          payload.order_interval_frequency = interval.frequency;
          payload.order_interval_unit = interval.unit;
        }
      }

      this.setLoading(true);
      this.resetMessage();

      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      })
        .then((r) => {
          if (r.ok) return r.json().catch(() => ({}));
          // Capture the server's error body so the exact reason is visible in the console.
          return r.text().then((body) => {
            const err = new Error('HTTP ' + r.status);
            err.status = r.status;
            err.body = body;
            throw err;
          });
        })
        .then(() => this.onAddSuccess())
        .catch((e) => {
          console.error('subscription-pdp: add failed', {
            status: e && e.status,
            response: e && e.body,
            payload: payload,
            error: e,
          });
          this.setLoading(false);
          this.showMessage(this.text.error, true);
        });
    }

    // Recharge needs minutes to reflect the product, so either confirm right away (instant
    // success, default) or lock the popup behind a spinner and poll until it really appears.
    onAddSuccess() {
      this.added = true;
      this.setFormDisabled(true);
      this.clearChargeSelection();
      this.confirmBtn.classList.remove('loading');
      this.confirmBtn.disabled = true;

      const submitted = this._submitted || {};
      const variant = submitted.variant || this.getSelectedVariant();
      const pending = {
        productId: String(this.productId),
        variantId: String(variant ? variant.id : ''),
        quantity: submitted.quantity || Math.max(1, parseInt(this.qtyInput.value, 10) || 1),
        chargeId: this.chargeIdOf(submitted.charge),
        addressId: submitted.charge ? submitted.charge.address_id : null,
        scheduledAt: submitted.charge ? submitted.charge.scheduled_at : null,
        ts: Date.now(),
      };
      // Count adds already awaiting confirmation for the same variant+order, so a repeat add
      // gets its own threshold instead of resolving on the first one's unit.
      pending.baseline = this.pendingQty(this.charges, pending) + this.reservedQty(pending);
      this._chargesTs = 0;

      if (this.autoclose) this.close();

      if (this.instantSuccess) {
        this.recordOptimistic(submitted, pending);
        if (!this.autoclose) {
          // As far as the customer is concerned the add is done, so unlock and repaint at once —
          // otherwise the popup would sit greyed out over a list that doesn't show the product.
          this.added = false;
          this.setFormDisabled(false);
          this.renderCharges();
          this.clearChargeSelection();
          this.confirmBtn.disabled = true;
          this.showSuccessMessage();
        }
        this.showDoneStatus();
        clearTimeout(this._statusHideTimer);
        this._statusHideTimer = setTimeout(() => this.hideStatus(), 8000);
        if (this.pollEnable) {
          this.addPending(pending);
          this.startPolling();
        }
        return;
      }

      if (!this.autoclose) this.showProcessingMessage();

      // Persist the pending add and poll until it lands whenever the status has to survive on
      // its own: polling is enabled OR the popup was autoclosed. The pending entry is stored in
      // localStorage and restored by resumePending(), so the on-page status survives a variant
      // switch that re-renders the element, a reconnect, or a full page reload — it can never be
      // stuck until the next reload. Otherwise fall back to a single delayed refresh.
      if (this.pollEnable || this.autoclose) {
        this.addPending(pending);
        this.showSyncingStatus();
        this.setTriggerDisabled(true);
        this.startPolling();
        return;
      }

      // Auto-check disabled (and popup kept open): one delayed refresh so the added product
      // shows up in the list and the form unlocks.
      clearTimeout(this._onceTimer);
      this._onceTimer = setTimeout(() => {
        this.fetchCharges().then(() => this.onPendingDone());
      }, 5000);
    }

    /* ---------- optimistic items (shown as added until Recharge reflects them) ---------- */

    optimisticKey() {
      return 'spdp_optimistic_' + (this.customerId || 'anon');
    }

    loadOptimistic() {
      let arr = null;
      try {
        const raw = window.localStorage.getItem(this.optimisticKey());
        arr = raw ? JSON.parse(raw) : null;
      } catch (e) {
        arr = null;
      }
      if (!Array.isArray(arr)) arr = this._optMem || [];
      const now = Date.now();
      const live = arr.filter((e) => now - (e.ts || 0) < OPTIMISTIC_MAX_MS);
      this._optMem = live;
      return live;
    }

    saveOptimistic(arr) {
      this._optMem = (arr || []).slice();
      try {
        if (arr && arr.length) {
          window.localStorage.setItem(this.optimisticKey(), JSON.stringify(arr));
        } else {
          window.localStorage.removeItem(this.optimisticKey());
        }
      } catch (e) {
        /* storage unavailable — the in-memory copy still covers this session */
      }
    }

    // Build the line item to fake, store it, and show it right away.
    recordOptimistic(submitted, pending) {
      const variant = submitted.variant;
      const charge = submitted.charge;
      if (!variant || !charge) return;
      const interval = submitted.isSubscription
        ? (submitted.group && submitted.group.interval) || this.resolveInterval(charge)
        : null;
      const unit = this.quotedUnitPrice(variant, pending.quantity, submitted.isSubscription);
      const arr = this.loadOptimistic();
      arr.push({
        chargeId: pending.chargeId,
        addressId: charge.address_id,
        scheduledAt: charge.scheduled_at,
        variantId: String(variant.id),
        baseline: pending.baseline,
        ts: Date.now(),
        item: {
          spdpOptimistic: true,
          title: this.dataset.productTitle || '',
          variant_title: variant.title || '',
          image: variant.image || null,
          quantity: pending.quantity,
          shopify_product_id: this.productId,
          shopify_variant_id: variant.id,
          type: submitted.isSubscription ? 'subscription' : 'onetime',
          order_interval_frequency: interval ? interval.frequency : null,
          order_interval_unit: interval ? interval.unit : null,
          unit_price: unit,
          total_price: unit != null ? unit * pending.quantity : null,
        },
      });
      this.saveOptimistic(arr);
      this.applyOptimistic();
    }

    // Re-insert every still-unreflected add (from any product) into its order, and forget the
    // ones real data now covers. Idempotent: previous fakes are stripped first.
    applyOptimistic() {
      (this.charges || []).forEach((c) => {
        if (Array.isArray(c.line_items)) c.line_items = c.line_items.filter((li) => !li.spdpOptimistic);
      });
      const entries = this.loadOptimistic();
      if (!entries.length) return;
      const stays = [];
      entries.forEach((e) => {
        const target = this.findCharge(e);
        if (!target) {
          stays.push(e);
          return;
        }
        const claimed = (e.baseline || 0) + ((e.item && e.item.quantity) || 1);
        if (this.variantTotalQty([target], e.variantId) >= claimed) return;
        if (!Array.isArray(target.line_items)) target.line_items = [];
        target.line_items.push(e.item);
        stays.push(e);
      });
      this.saveOptimistic(stays);
    }

    // Match a stored entry to an order by id, falling back to date + address so this also
    // works before the backend exposes charge_id.
    findCharge(entry, charges) {
      const list = charges || this.charges || [];
      if (entry.chargeId) {
        const byId = list.find((c) => String(this.chargeIdOf(c)) === String(entry.chargeId));
        if (byId) return byId;
      }
      return (
        list.find(
          (c) =>
            String(c.address_id) === String(entry.addressId) &&
            String(c.scheduled_at) === String(entry.scheduledAt)
        ) || null
      );
    }

    showSuccessMessage() {
      this.message.hidden = false;
      this.message.className = 'subscription-pdp__message subscription-pdp__message_success';
      this.message.innerHTML =
        '<span class="subscription-pdp__message-text"><strong>' + this.escape(this.text.success) + '</strong></span>';
    }

    showProcessingMessage() {
      this.message.hidden = false;
      this.message.className = 'subscription-pdp__message subscription-pdp__message_processing';
      this.message.innerHTML =
        '<span class="subscription-pdp__spinner" aria-hidden="true"></span>' +
        '<span class="subscription-pdp__message-text">' +
        '<strong>' + this.escape(this.text.success) + '</strong>' +
        '<span class="subscription-pdp__message-note">' + this.escape(this.text.processing) + '</span>' +
        '</span>';
    }

    /* ---------- pending state (localStorage + polling) ---------- */

    pendingKey() {
      return 'spdp_pending_' + (this.customerId || 'anon');
    }

    loadPending() {
      let arr = null;
      try {
        const raw = window.localStorage.getItem(this.pendingKey());
        arr = raw ? JSON.parse(raw) : null;
      } catch (e) {
        arr = null;
      }
      if (!Array.isArray(arr)) arr = this._mem || [];
      // Drop very old leftovers (e.g. another product never revisited) so storage can't grow.
      const now = Date.now();
      const pruned = arr.filter((e) => now - (e.ts || 0) < 86400000);
      this._mem = pruned;
      return pruned;
    }

    savePending(arr) {
      this._mem = (arr || []).slice();
      try {
        if (arr && arr.length) {
          window.localStorage.setItem(this.pendingKey(), JSON.stringify(arr));
        } else {
          window.localStorage.removeItem(this.pendingKey());
        }
      } catch (e) {
        /* storage unavailable — in-memory fallback keeps the current session working */
      }
    }

    addPending(entry) {
      const arr = this.loadPending();
      arr.push(entry);
      this.savePending(arr);
    }

    // Qty of earlier adds for the same variant+order that Recharge hasn't confirmed yet.
    reservedQty(target) {
      const source = this.instantSuccess ? this.loadOptimistic() : this.loadPending();
      return source
        .filter((e) => String(e.variantId) === String(target.variantId) && this.sameTarget(e, target))
        .reduce((sum, e) => sum + ((e.item ? e.item.quantity : e.quantity) || 1), 0);
    }

    sameTarget(a, b) {
      if (a.chargeId && b.chargeId) return String(a.chargeId) === String(b.chargeId);
      return String(a.addressId) === String(b.addressId) && String(a.scheduledAt) === String(b.scheduledAt);
    }

    // Qty a pending add is measured against: within its target order when known, else overall.
    pendingQty(charges, entry) {
      if (!entry.chargeId && !entry.scheduledAt) return this.variantTotalQty(charges, entry.variantId);
      const charge = this.findCharge(entry, charges);
      return this.variantTotalQty(charge ? [charge] : charges, entry.variantId);
    }

    // Quantity of a variant scheduled across the given orders. Faked items never count —
    // this is the signal for whether Recharge has really applied an add.
    variantTotalQty(charges, variantId) {
      let total = 0;
      (charges || []).forEach((c) =>
        (c.line_items || []).forEach((li) => {
          if (li.spdpOptimistic) return;
          if (String(li.shopify_variant_id) === String(variantId)) total += li.quantity || 0;
        })
      );
      return total;
    }

    // Called after charges load (initial + each poll). Resolves pending entries for this
    // product when the variant count grows past its baseline, or when the hard timeout hits.
    refreshPendingState(charges) {
      const mine = this.loadPending().filter((e) => String(e.productId) === String(this.productId));
      if (!mine.length) {
        this.stopPolling();
        // If a spinner was showing (e.g. storage was cleared), resolve the UI so it can't hang.
        if (this._syncing) this.onPendingDone();
        return;
      }

      const now = Date.now();
      const resolved = [];
      const stillPending = [];
      mine.forEach((e) => {
        const appeared = this.pendingQty(charges, e) >= (e.baseline || 0) + (e.quantity || 1);
        const expired = now - (e.ts || 0) > PENDING_MAX_MS;
        (appeared || expired ? resolved : stillPending).push(e);
      });

      if (resolved.length) {
        const stays = this.loadPending().filter(
          (e) => !resolved.some((r) => r.ts === e.ts && String(r.variantId) === String(e.variantId))
        );
        this.savePending(stays);
      }

      if (stillPending.length) {
        // Instant mode already confirmed the add — poll silently, no spinner, trigger stays live.
        if (!this.instantSuccess) {
          this.showSyncingStatus();
          this.setTriggerDisabled(true);
        }
        this.startPolling();
      } else {
        this.stopPolling();
        this.onPendingDone();
      }
    }

    startPolling() {
      if (this._pollTimer) return;
      this._pollTimer = setInterval(() => {
        this.fetchCharges(this.pendingChargeId()).then((charges) => this.refreshPendingState(charges));
      }, this.instantSuccess ? POLL_BACKGROUND_MS : POLL_MS);
    }

    // Re-read only one order when a single pending add targets it; otherwise read them all.
    pendingChargeId() {
      const mine = this.loadPending().filter(
        (e) => String(e.productId) === String(this.productId) && e.chargeId
      );
      return mine.length === 1 ? mine[0].chargeId : null;
    }

    stopPolling() {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }

    // On load (or when the element re-connects after a variant-driven re-render), resume any
    // pending sync for this product. Active when polling is enabled OR autoclose is on — the
    // latter persists its status the same way so it survives re-renders and reloads.
    resumePending() {
      if (!this.pollEnable && !this.autoclose) return;
      this.refreshPendingState(this.charges);
    }

    onPendingDone() {
      const wasAdded = this.added;
      this._syncing = false;
      this.added = false;
      this.setTriggerDisabled(false);
      this.setFormDisabled(false);

      // Refresh only a popup still showing our own post-add state. In instant mode the trigger
      // stays live, so the customer may have reopened it and started a new selection by now —
      // that must not be cleared from under them.
      if (this.isOpen() && wasAdded) {
        this.renderCharges();
        this.clearChargeSelection();
        this.confirmBtn.disabled = true;
        this.showSuccessMessage();
      }

      // Instant mode showed and scheduled its own status already.
      if (this.instantSuccess) return;
      this.showDoneStatus();
      clearTimeout(this._statusHideTimer);
      this._statusHideTimer = setTimeout(() => this.hideStatus(), 6000);
    }

    clearChargeSelection() {
      this.selectedGroupIndex = null;
      if (this.chargesBox) {
        this.chargesBox.querySelectorAll('.subscription-pdp__charge-radio').forEach((r) => {
          r.checked = false;
        });
      }
    }

    /* ---------- inline status (near the trigger, survives reloads) ---------- */

    showSyncingStatus() {
      this._syncing = true;
      if (!this.statusBox) return;
      this.statusBox.hidden = false;
      this.statusBox.className = 'subscription-pdp__status subscription-pdp__status_syncing';
      this.statusBox.innerHTML =
        '<span class="subscription-pdp__spinner" aria-hidden="true"></span>' +
        '<span>' + this.escape(this.text.processing) + '</span>';
    }

    showDoneStatus() {
      if (!this.statusBox) return;
      this.statusBox.hidden = false;
      this.statusBox.className = 'subscription-pdp__status subscription-pdp__status_done';
      this.statusBox.innerHTML = '<span>' + this.escape(this.text.success) + '</span>';
    }

    hideStatus() {
      this._syncing = false;
      if (!this.statusBox) return;
      this.statusBox.hidden = true;
      this.statusBox.innerHTML = '';
    }

    setTriggerDisabled(state) {
      if (!this.trigger) return;
      this.trigger.disabled = state;
      this.trigger.classList.toggle('subscription-pdp__trigger_disabled', state);
    }

    // Lock/unlock the interactive controls (qty, toggle, charge radios) to prevent
    // repeated adds while a request is in flight or after a successful add.
    setFormDisabled(state) {
      this._formLocked = state;
      this.toggleInput.disabled = state || (this._typeMode != null && this._typeMode !== 'toggle');
      this.qtyInput.disabled = state;
      this.popup.querySelectorAll('[data-spdp-qty]').forEach((b) => (b.disabled = state));
      this.chargesBox.querySelectorAll('.subscription-pdp__charge-radio').forEach((r) => (r.disabled = state));
      // Visually dim the locked controls so it's clear they're inactive while syncing.
      this.popup.classList.toggle('is-locked', state);
    }

    // Recharge groups subscriptions by date; reuse the interval of the chosen order.
    resolveInterval(charge) {
      const items = charge.line_items || [];
      const sub = items.find((li) => li.type === 'subscription' && li.order_interval_frequency);
      const source = sub || items.find((li) => li.order_interval_frequency);
      if (!source) return null;
      return {
        frequency: source.order_interval_frequency,
        unit: source.order_interval_unit,
      };
    }

    setLoading(state) {
      this.confirmBtn.classList.toggle('loading', state);
      this.confirmBtn.disabled = state || this.added || this.selectedGroupIndex == null;
    }

    showMessage(text, isError) {
      this.message.textContent = text;
      this.message.classList.toggle('subscription-pdp__message_error', isError);
      this.message.classList.toggle('subscription-pdp__message_success', !isError);
      this.message.hidden = false;
    }

    resetMessage() {
      this.message.hidden = true;
      this.message.textContent = '';
    }

    /* ---------- helpers ---------- */

    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return this.escape(value);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }

    escape(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  customElements.define('subscription-pdp', SubscriptionPdp);
})();
